import { yupResolver } from '@hookform/resolvers/yup';
import React, { KeyboardEventHandler, useEffect, useState } from 'react';
import { Col, Form, Row } from 'react-bootstrap';
import { Controller, useFieldArray, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import CreatableSelect from 'react-select/creatable';
import { toast } from 'react-toastify';
import { v4 as uuidv4 } from 'uuid';
import * as Yup from 'yup';

import { useAppSelector } from '@/app/hook';
import { PSButton } from '@/components/ui/button';
import { PSInput } from '@/components/ui/input';
import { AlertsType } from '@/constants/alerts.constant';
import { selectUseId } from '@/features/auth/auth.slice';
import { BlockType } from '@/interfaces/models/Knowledge/scenario.interface';

import AddNoteButton from '../../AddNoteButton';
import NodeParentChanger from '../../NodeParentChanger';
import NoteForm, { getNoteSchema, INoteForm } from '../../NoteForm';
import ScenarioHeader from '../../ScenarioHeader';
import { MacBotActionKind } from '../../types';
import VideoScenarioDeleteButton from './video-scenario-delete-button';
import ScenarioSide from './video-scenario-side';

export const MacBotUserInputForm = ({
  setShow,
  itemData,
  parentId,
  tree,
  loading,
  createScenarioBlock,
  deleteScenarioBlock,
  changeScenarioBlock,
}: {
  setShow: (item: boolean) => void;
  itemData: any;
  loading: boolean;
  createScenarioBlock: (block: any) => Promise<any>;
  deleteScenarioBlock: (block: any) => void;
  changeScenarioBlock: (block: any) => Promise<any>;
  parentId: string | null;
  tree: any[];
}) => {
  const { t } = useTranslation();
  const components = {
    DropdownIndicator: null,
  };
  const userId = useAppSelector(selectUseId);

  interface IUserInputForm extends INoteForm {
    BlockTitle: string;
    Keywords: string[];
  }

  const schema = Yup.object({
    Keywords: Yup.array().min(1, t('MacBotUserInputForm/er-01')),
    ...getNoteSchema(t),
  });

  const [isDelete, setIsDelete] = useState(true);
  useEffect(() => {
    if (!itemData) {
      setIsDelete(false);
    }
  }, [itemData]);

  const {
    register,
    formState: { errors, isSubmitted },
    reset,
    watch,
    trigger,
    getValues,
    setValue,
    handleSubmit,
    control,
  } = useForm<IUserInputForm>({
    defaultValues: {
      BlockTitle: '',
      Keywords: [],
      notes: [
        {
          note_id: uuidv4(),
          creation_date_time: new Date().toISOString(),
          title: '',
          description: '',
          qc: false,
          author: userId,
        },
      ],
    },
    resetOptions: {
      keepDefaultValues: true,
    },
    resolver: yupResolver(schema) as any,
    mode: 'onSubmit',
  });
  const { fields } = useFieldArray({
    control,
    name: 'notes',
  });

  useEffect(() => {
    reset();
    setChangedValue([]);
  }, []);

  useEffect(() => {
    if (itemData) {
      if (itemData?.title) setValue('BlockTitle', itemData?.title);
      if (itemData?.keywords) {
        setValue('Keywords', itemData.keywords);
        setChangedValue(
          itemData.keywords.map((keyword: string) => {
            return {
              label: keyword,
              value: keyword,
            };
          })
        );
      }
      setValue('notes', itemData?.notes);
    }
  }, [itemData]);

  const deleteHandler = () => {
    deleteScenarioBlock(itemData);
    setShow(false);
  };

  const ButtonSubmit = (data: any) => {
    if (itemData) {
      const _data = {
        ...itemData,
        id: itemData.id,
        type: BlockType.UserInput,
        title: data.Keywords[0],
        parent: itemData.parent,
        keywords: data.Keywords,
        notes: data.notes,
      };
      changeScenarioBlock(_data).then(() => {
        reset();
        setShow(false);
      });
    } else {
      const _data = {
        type: BlockType.UserInput,
        title: data.Keywords[0],
        parent: parentId,
        keywords: data.Keywords,
        notes: data.notes,
      };
      createScenarioBlock(_data).then(() => {
        reset();
        setShow(false);
      });
    }
  };

  interface Option {
    readonly label: string;
    readonly value: string;
  }

  const createOption = (label: string) => ({
    label,
    value: label,
  });

  const [inputValue, setInputValue] = React.useState('');
  const [changedValue, setChangedValue] = React.useState<readonly Option[]>([]);

  const handleKeyDown: KeyboardEventHandler = event => {
    if (!inputValue) return;
    switch (event.key) {
      case 'Enter':
      case 'Tab':
        if (changedValue?.some((dr: any) => dr.value == inputValue)) {
          toast.error(t(AlertsType.ISKBKO027));
        }
        setChangedValue(prev => {
          if (prev?.some((dr: any) => dr.value == inputValue)) {
            return prev;
          }
          setValue(
            'Keywords',
            [...prev, createOption(inputValue)].map(dr => dr.value)
          );
          return [...prev, createOption(inputValue)];
        });

        setInputValue('');
        event.preventDefault();
    }
  };

  const addInput = () => {
    if (!inputValue) return;

    if (changedValue?.some((dr: any) => dr.value == inputValue)) {
      toast.error(t(AlertsType.ISKBKO027));
    }

    setChangedValue(prev => {
      if (prev?.some((dr: any) => dr.value == inputValue)) {
        return prev;
      }
      setValue(
        'Keywords',
        [...prev, createOption(inputValue)].map(dr => dr.value)
      );
      return [...prev, createOption(inputValue)];
    });

    setInputValue('');
  };

  useEffect(() => {
    if (isSubmitted) {
      trigger();
    }
  }, [changedValue]);

  return (
    <>
      <ScenarioSide size="sm" onSubmit={handleSubmit(ButtonSubmit)}>
        <ScenarioHeader
          type={MacBotActionKind.ShowUserInputForm}
          onClose={() => {
            reset();
          }}
        />

        <div className="tw-mt-5">
          <Row className="m-0">
            <Col lg="12" className="px-3">
              <Form.Group>
                <Form.Label className="tw-text-[#687076] tw-text-xs tw-font-normal tw-font-['Lexend'] tw-leading-none">
                  {t('MacBotUserInputForm/l-03')}
                </Form.Label>

                <Controller
                  control={control}
                  name="Keywords"
                  render={({ field: { onChange, onBlur, value, name, ref }, fieldState: { invalid, isTouched, isDirty, error }, formState }) => {
                    return (
                      <div>
                        <div className={`tw-flex tw-row tw-gap-3 ${watch('Keywords').length > 0 ? 'tw-mb-5' : ''}`}>
                          <PSInput
                            type="text"
                            className="tw-w-full tw-h-11 tw-px-4 tw-py-3 tw-bg-white tw-rounded-md tw-shadow tw-border tw-border-solid tw-border-gray-300"
                            placeholder={t('MacBotUserInputForm/ph-01')}
                            onFocus={e => (e.target.placeholder = '')}
                            onBlur={e => (e.target.placeholder = t('MacBotUserInputForm/ph-01'))}
                            value={inputValue}
                            onChange={newValue => {
                              setInputValue(newValue.target.value);
                            }}
                            onKeyDown={e => {
                              handleKeyDown(e);
                            }}
                          />
                        </div>

                        {watch('Keywords').length > 0 && (
                          <CreatableSelect
                            placeholder=""
                            className="customSelect-displayInput"
                            components={components}
                            isClearable
                            isMulti
                            inputValue=""
                            menuIsOpen={false}
                            onBlur={onBlur}
                            ref={ref}
                            onChange={newValue => {
                              setChangedValue(newValue);
                              setValue(
                                'Keywords',
                                newValue.map(dr => dr?.value)
                              );
                              onChange(newValue?.map(dr => dr?.value as string));
                            }}
                            value={changedValue}
                          />
                        )}
                      </div>
                    );
                  }}
                />

                <Form.Control.Feedback type="invalid" className="d-block font-poppins">
                  {errors.Keywords?.message}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>
            <Col lg="12">
              <hr className="tw-bg-gray-200 tw-text-gray-200" />
            </Col>
          </Row>
        </div>
        <div className="tw-flex flex-row tw-justify-between tw-items-center">
          <div className="tw-flex tw-justify-start tw-items-center tw-gap-2">
            {<VideoScenarioDeleteButton itemData={itemData} tree={tree} deleteHandler={deleteHandler} />}

            {itemData?.id && <NodeParentChanger tree={tree} currentId={itemData.id} />}
            <AddNoteButton noteFormMethods={{ getValues, setValue }} />
          </div>
          <div className="tw-flex tw-justify-end">
            <PSButton id="btn-user-submit" type="submit" disabled={loading} isLoading={loading} title={t('MacBotResponseForm/btn-02')} />
          </div>
        </div>
        <NoteForm noteFormMethods={{ register, watch, errors, fields, setValue }} />
      </ScenarioSide>
    </>
  );
};
